import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, shallowRef, triggerRef, watch } from 'vue';
import {
  IconArrowRight,
  IconCheck,
  IconCloudDownload,
  IconExclamationCircle,
  IconMicroscope,
  IconWifi,
} from '@tabler/icons-vue';

import { fetchUrlPreviewImage } from '@/api/media';
import { isAbortError, requestMultipart } from '@/api/client';
import { extractDataFromURL, extractImagesFromURL } from '@/api/order';
import type { ImageObject } from '@/types/types';
import { isValidUrlRFC3986StartsWithHttpOrHttps } from '@/func/string/url';
import translator from '@/locale/translator';
import type { ScanTimelineItem } from '@/components/orders/wizard_sections/WizardScanTimeline.vue';
import { useOrderCreationStore } from '@/stores/orderCreation';

export const useImportOrderCreationStore = defineStore('importOrderCreation', () => {
  const orderCreationStore = useOrderCreationStore();
  const { order } = storeToRefs(orderCreationStore);

  /* ----------------------- ANALYSIS STATE VARIABLES ----------------------- */
  const webImportUrl = ref<string>('');
  const jobImportBlocks = ref<any>(null);
  const jobImportBlocksPromise = ref<Promise<void> | null>(null);
  const fileImports = ref<any[]>([]);
  const fileUploadPercentage = ref(0);
  const fileIsUploading = ref(false);
  const fileIsExtracting = ref(false);
  const fileUploaded = ref(false);
  const fileExtractionPromise = ref<Promise<ImageObject> | null>(null);
  const fileExtractionError = ref<unknown>(null);
  const previewImageLoaded = ref(false);
  const previewImageUrl = ref<string | undefined>();
  const didScanJobPosting = ref(false);
  const scanningInProgress = ref(false);
  const scanTimeline = shallowRef<ScanTimelineItem[]>([]);
  const scanTimelineIsDone = shallowRef(false);
  const statusMessages = ref<ScanTimelineItem[]>([]);
  const statusMessagesIsDone = ref(false);

  let activeExtractionRequestId = 0;
  let extractionAbortController: AbortController | null = null;
  let fileUploadAbortController: AbortController | null = null;
  let fileImportTimer: ReturnType<typeof setTimeout> | null = null;
  let activeExtractionPromise: Promise<void> | null = null;

  /* --------------------------- COMPUTED VALUES --------------------------- */
  const currentSubtitle = computed(() => {
    return scanTimelineIsDone.value ? 'orders.wizard.analytics.subtitle_completed' : 'orders.wizard.analytics.subtitle';
  });

  /* -------------------------------- ACTIONS ------------------------------- */

  function nextExtractionRequestId() {
    activeExtractionRequestId += 1;
    return activeExtractionRequestId;
  }

  function isActiveExtractionRequest(requestId: number) {
    return activeExtractionRequestId === requestId;
  }

  function cancelActiveExtraction() {
    nextExtractionRequestId();
    extractionAbortController?.abort();
    extractionAbortController = null;
    jobImportBlocksPromise.value = null;
    activeExtractionPromise = null;

    if (fileImportTimer) {
      clearTimeout(fileImportTimer);
      fileImportTimer = null;
    }
  }

  function cancelFileImport() {
    cancelActiveExtraction();
    fileUploadAbortController?.abort();
    fileUploadAbortController = null;
    fileImports.value = [];
    jobImportBlocks.value = null;
    jobImportBlocksPromise.value = null;
    resetFileUploadState();
    resetAnalysisState();
  }

  /**
   * Fetch URL preview image for the job posting
   */
  const fetchPostingPreview = (targetUrl: string, requestId: number, signal: AbortSignal) => {
    if (isValidUrlRFC3986StartsWithHttpOrHttps(targetUrl ?? '')) {
      fetchUrlPreviewImage(targetUrl, { signal })
        .then((response) => {
          if (!isActiveExtractionRequest(requestId)) {
            return;
          }

          previewImageUrl.value = response.data['800x600'];
        })
        .catch((e) => {
          if (!isAbortError(e)) {
            console.error(e);
          }
        });
    }
  };

  /**
   * Spawn status messages with a fake progressive feel
   */
  const spawnStatusMessages = (requestId: number) => {
    let messageIndex = 0;
    const renderStatusMessages = () => {
      if (!isActiveExtractionRequest(requestId) || scanTimelineIsDone.value) {
        return;
      }

      const currentMessage = statusMessages.value[messageIndex];
      if (currentMessage) {
        if (currentMessage.title === `${translator.get('common.done')}!`) {
          scanTimelineIsDone.value = true;
          scanningInProgress.value = false;
        }

        addScanTimelineItem(currentMessage.title, currentMessage.icon, currentMessage.isError);
        messageIndex++;
      }

      setTimeout(renderStatusMessages, 1000);
    };

    renderStatusMessages();
  };

  /**
   * Add an item to the scan timeline
   */
  function addScanTimelineItem(title: string, icon: any, isError?: boolean) {
    scanTimeline.value = [...scanTimeline.value, { title, icon, isError }];
    triggerRef(scanTimeline);
  }

  async function fetchImportFromURL(targetUrl: string, requestId: number, signal: AbortSignal) {
    // don't scan it twice..
    if (didScanJobPosting.value || !targetUrl) {
      return;
    }

    scanningInProgress.value = true;

    const url = 'https://' + targetUrl;

    fetchPostingPreview(url, requestId, signal);

    extractImagesFromURL(url, { signal })
      .then((response) => {
        if (!isActiveExtractionRequest(requestId)) {
          return;
        }
        order.value.bot_program_id = response.bot_program_id;
        order.value.bot_id = response.bot_id;
      })
      .catch((e) => {
        if (!isAbortError(e)) {
          console.error('Error extracting images from URL:', e);
        }
      });

    jobImportBlocksPromise.value = extractDataFromURL(url, 'block', { signal })
      .then((webImportBlocks) => {
        if (!isActiveExtractionRequest(requestId)) {
          return;
        }
        jobImportBlocks.value = webImportBlocks?.data || [];
      })
      .catch((e) => {
        if (!isAbortError(e)) {
          console.error('Error fetching block import:', e);
        }
      });

    try {
      const extractDataResponse = await extractDataFromURL(url, null, { signal });

      if (!isActiveExtractionRequest(requestId)) {
        return;
      }

      const extractedData = extractDataResponse.data;
      order.value.rte_blocks = extractDataResponse.block || extractDataResponse.data.block || [];

      // bot_program_id is at the top level of the response, not inside `data`
      if (extractDataResponse.bot_program_id) {
        order.value.bot_program_id = String(extractDataResponse.bot_program_id);
      }

      showImportedDataMessages(extractedData);
    } catch (e) {
      if (!isActiveExtractionRequest(requestId)) return;
      if (isAbortError(e)) throw e;

      scanningInProgress.value = false;
      showImportedDataMessages({});
      didScanJobPosting.value = true;
    }

    order.value.directlink = url;
  }

  async function uploadAndExtractFile(
    file: File,
    onProgress: (loaded: number, total: number) => void
  ): Promise<ImageObject> {
    fileUploadPercentage.value = 0;
    fileIsUploading.value = true;
    fileIsExtracting.value = false;
    fileUploaded.value = false;
    fileExtractionError.value = null;

    const formData = new FormData();
    formData.append('file', file);
    const abortController = new AbortController();
    fileUploadAbortController = abortController;

    const promise = requestMultipart('POST', '/v1/account/order/extract_from_file', {}, formData, {
      signal: abortController.signal,
      onUploadProgress: (loaded, total) => {
        fileUploadPercentage.value = Math.round((loaded / total) * 100);
        onProgress(loaded, total);
        if (loaded === total && fileIsUploading.value) {
          fileIsUploading.value = false;
          fileIsExtracting.value = true;
          fileUploaded.value = true;
        }
      },
    })
      .then((res: { data: ImageObject }) => {
        fileIsExtracting.value = false;
        return res.data;
      })
      .catch((err) => {
        fileIsUploading.value = false;
        fileIsExtracting.value = false;
        if (isAbortError(err)) {
          throw err;
        }
        fileExtractionError.value = err;
        throw err;
      })
      .finally(() => {
        if (fileUploadAbortController === abortController) {
          fileUploadAbortController = null;
        }
      });

    fileExtractionPromise.value = promise;
    return promise;
  }

  async function fetchImportFromFile(requestId: number, signal: AbortSignal) {
    const promise = fileExtractionPromise.value;
    const preExtracted = fileImports.value[0];
    if (!promise && (!preExtracted || typeof preExtracted !== 'object')) {
      return;
    }

    scanningInProgress.value = true;
    previewImageLoaded.value = true;

    try {
      let result: any;
      if (promise) {
        result = await new Promise((resolve, reject) => {
          const abortHandler = () => reject(new DOMException('The operation was aborted.', 'AbortError'));
          if (signal.aborted) return abortHandler();
          signal.addEventListener('abort', abortHandler, { once: true });
          promise.then(
            (data) => {
              signal.removeEventListener('abort', abortHandler);
              resolve(data);
            },
            (err) => {
              signal.removeEventListener('abort', abortHandler);
              reject(err);
            }
          );
        });
      } else {
        result = preExtracted;
      }

      if (!isActiveExtractionRequest(requestId)) return;

      if (typeof result !== 'object' || result === null) {
        throw new Error('Invalid data received while extracting from file.');
      }

      order.value.rte_blocks = result.block || [];
      showImportedDataMessages(result.meta);
      didScanJobPosting.value = true;
    } catch (e) {
      if (!isActiveExtractionRequest(requestId)) return;
      if (isAbortError(e)) throw e;

      console.error('Error extracting data from file:', e);
      scanningInProgress.value = false;
      showImportedDataMessages({});
      didScanJobPosting.value = true;
    }
  }

  function showImportedDataMessages(extractedData: any) {
    scanTimelineIsDone.value = false;

    const hasTitle = !!(extractedData.job_title_full || extractedData.job_title);
    const hasCompany = !!extractedData.job_company;
    const hasWorkplace = !!extractedData.job_workplace;

    if (hasTitle) {
      order.value.title = extractedData.job_title_full || extractedData.job_title;
    }

    if (hasCompany) {
      order.value.company_name = extractedData.job_company;
    }

    if (hasWorkplace) {
      order.value.workplace_location = extractedData.job_workplace;
    }

    // the posting's own language, so generated content and the ad match it instead of defaulting to German
    if (extractedData.job_language) {
      order.value.language = extractedData.job_language;
    }

    const workloadMin = parseInt(extractedData.job_workload_min);
    const workloadMax = parseInt(extractedData.job_workload_max);

    if (workloadMin > 0) {
      order.value.workload_min = extractedData.job_workload_min;
    }

    if (workloadMax > 0) {
      order.value.workload_max = extractedData.job_workload_max;
    }

    if (workloadMin !== 100 || workloadMax !== 100) {
      order.value.workload_fulltime = false;
    }

    if (extractedData.job_employment_type) {
      order.value.workload_permanent = extractedData.job_employment_type === 'temporary' ? false : true;
    }

    const errorFallback = translator.get('orders.wizard.analytics.failed_to_retrieve');

    statusMessages.value.push(
      {
        title: `${translator.get('common.title')}: ${hasTitle ? order.value.title : errorFallback}`,
        icon: hasTitle ? IconArrowRight : IconExclamationCircle,
        isError: !hasTitle,
      },
      {
        title: `${translator.get('common.company')}: ${hasCompany ? order.value.company_name : errorFallback}`,
        icon: hasCompany ? IconArrowRight : IconExclamationCircle,
        isError: !hasCompany,
      },
      {
        title: `${translator.get('common.workspace')}: ${hasWorkplace ? order.value.workplace_location : errorFallback}`,
        icon: hasWorkplace ? IconArrowRight : IconExclamationCircle,
        isError: !hasWorkplace,
      }
    );
    statusMessages.value.push({ title: `${translator.get('common.done')}!`, icon: IconCheck });
    statusMessagesIsDone.value = true;
  }

  /**
   * Initialize status messages for the analysis step
   */
  function initializeImportStep() {
    statusMessages.value = [
      { title: translator.get('orders.wizard.analytics.connecting'), icon: IconWifi },
      { title: translator.get('orders.wizard.analytics.crawling'), icon: IconCloudDownload },
      { title: translator.get('orders.wizard.analytics.analyzing'), icon: IconMicroscope },
    ];
  }

  /**
   * Reset all analysis-related state
   */
  function resetAnalysisState() {
    if (activeExtractionPromise) return;
    previewImageLoaded.value = false;
    previewImageUrl.value = undefined;
    didScanJobPosting.value = false;
    statusMessages.value = [];
    scanTimeline.value = [];
    scanTimelineIsDone.value = false;
    statusMessagesIsDone.value = false;
    scanningInProgress.value = false;
  }

  function resetFileUploadState() {
    fileUploadAbortController = null;
    fileUploadPercentage.value = 0;
    fileIsUploading.value = false;
    fileIsExtracting.value = false;
    fileUploaded.value = false;
    fileExtractionPromise.value = null;
    fileExtractionError.value = null;
  }

  function resetState() {
    webImportUrl.value = '';
    fileImports.value = [];
    jobImportBlocks.value = null;
    jobImportBlocksPromise.value = null;
    resetFileUploadState();
    resetAnalysisState();
  }

  /**
   * Handle extraction step logic (fetches and assigns data from URL or file)
   */
  const handleExtraction = async (): Promise<void> => {
    if (didScanJobPosting.value || orderCreationStore.skipImportStep) {
      return;
    }

    if (activeExtractionPromise) {
      return activeExtractionPromise;
    }

    const requestId = nextExtractionRequestId();
    extractionAbortController = new AbortController();
    const signal = extractionAbortController.signal;

    initializeImportStep();
    spawnStatusMessages(requestId);

    activeExtractionPromise = (async () => {
      try {
        if (order.value.creation_source === 'direct_url')
          await fetchImportFromURL(orderCreationStore.order.directlink || '', requestId, signal);
        else if (order.value.creation_source === 'web_import')
          await fetchImportFromURL(webImportUrl.value || '', requestId, signal);
        else if (order.value.creation_source === 'file_import') await fetchImportFromFile(requestId, signal);
      } catch (e) {
        if (isAbortError(e)) {
          return;
        }
      } finally {
        if (isActiveExtractionRequest(requestId)) {
          extractionAbortController = null;
          activeExtractionPromise = null;
        }
      }
    })();

    return activeExtractionPromise;
  };

  /* -------------------------------- WATCHERS ------------------------------ */

  // Watch the image URL to preload it and handle loading state
  watch(
    () => previewImageUrl.value,
    (newVal) => {
      if (!newVal) {
        return;
      }

      previewImageLoaded.value = false;
      const img = new Image();
      img.src = newVal;
      img.onload = () => (previewImageLoaded.value = true);
      didScanJobPosting.value = true;
    }
  );

  watch(
    [() => fileImports.value, () => webImportUrl.value, () => orderCreationStore.order.directlink],
    resetAnalysisState
  );

  watch(statusMessagesIsDone, (newVal) => {
    orderCreationStore.availableSteps.import.canGoNext = newVal;
  });

  return {
    // State
    webImportUrl,
    jobImportBlocks,
    jobImportBlocksPromise,
    fileImports,
    previewImageLoaded,
    previewImageUrl,
    didScanJobPosting,
    scanningInProgress,
    scanTimeline,
    scanTimelineIsDone,
    statusMessages,
    statusMessagesIsDone,
    fileUploadPercentage,
    fileIsUploading,
    fileIsExtracting,
    fileUploaded,
    fileExtractionPromise,
    fileExtractionError,

    // Computed
    currentSubtitle,

    // Actions
    resetState,
    resetFileUploadState,
    addScanTimelineItem,
    resetAnalysisState,
    handleExtraction,
    cancelActiveExtraction,
    cancelFileImport,
    uploadAndExtractFile,
  };
});
